import React, { type FC, useMemo, useState } from 'react';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { ChevronDown, Users } from 'lucide-react';

import NavigationLink from 'src/components/layout/landlord/NavigationLink';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { EPresence, isUserAway } from 'src/api/users-api';
import PresenceDot from 'src/components/PresenceDot';
import { useTeamMembers } from 'src/services/api/team/members/use-team-members';

import styles from 'src/components/layout/landlord/DesktopNavigationTeamLink.module.css';

type DisplayMember = {
    uuid: string;
    name: string;
    profilePhotoUrl?: string | null;
    online: boolean;
    presence: EPresence;
};

type Props = {
    onNavigationChange: () => void;
    isSidebarCollapsed: boolean;
};

export const DesktopNavigationTeamLink: FC<Props> = ({ onNavigationChange, isSidebarCollapsed }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const { data: members } = useTeamMembers({ refetchInterval: 60_000 });

    const sortedMembers = useMemo<DisplayMember[]>(() => {
        const displayMembers: DisplayMember[] = (members ?? []).map((member) => ({
            uuid: member.uuid,
            name: member.name,
            profilePhotoUrl: member.profile_photo_url,
            // Count/sort: only members explicitly online (undefined presence → not online).
            online: member.presence?.value === EPresence.ONLINE,
            // Dot display: match the sidebar pattern (undefined presence → show as online).
            presence: isUserAway(member) ? EPresence.AWAY : EPresence.ONLINE,
        }));
        // Show online members first so the most relevant people are at the top.
        return displayMembers.sort((a, b) => Number(b.online) - Number(a.online));
    }, [members]);

    const onlineCount = sortedMembers.filter((member) => member.online).length;

    const toggleExpanded = (event: React.MouseEvent) => {
        // Stop the click from bubbling to the navigation button behind it.
        event.stopPropagation();
        setIsExpanded((prev) => !prev);
    };

    return (
        <>
            <Box position="relative">
                {/* Row click keeps the normal navigation to the Team page. */}
                <NavigationLink
                    text={isSidebarCollapsed ? '' : (
                        <>
                            Team <span className={styles.count}>{onlineCount} online</span>
                        </>
                    )}
                    icon={<Users size={20} />}
                    link="/landlord/inner/team"
                    onClick={onNavigationChange}
                    testId="team-link"
                />
                {!isSidebarCollapsed && (
                    <IconButton
                        size="small"
                        className={styles.expandButton}
                        onClick={toggleExpanded}
                        aria-label={isExpanded ? 'Collapse team members' : 'Expand team members'}
                        aria-expanded={isExpanded}
                        data-testid="toggle-team-expand"
                    >
                        <ChevronDown
                            size={18}
                            className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
                        />
                    </IconButton>
                )}
            </Box>
            {!isSidebarCollapsed && isExpanded && (
                <List className={styles.memberList} data-testid="team-online-list">
                    {sortedMembers.map((member) => (
                        <li key={member.uuid} className={styles.member}>
                            <span className={styles.avatarWrap}>
                                <ProfileImageRounded
                                    url={member.profilePhotoUrl}
                                    userName={member.name}
                                    width={22}
                                    height={22}
                                />
                                <span className={styles.presenceDot}>
                                    <PresenceDot presence={member.presence} size={7} withBorder />
                                </span>
                            </span>
                            <span className={styles.memberName}>{member.name}</span>
                        </li>
                    ))}
                </List>
            )}
        </>
    );
};
